// ---------------------------------------------------------------------------
// Timeline Renderer
// ---------------------------------------------------------------------------
// Engagement primitive — publishes the editor `Timeline` block as a vertical
// list of chronological milestones parsed from the pipe-delimited textarea
// the Puck editor uses ("Date | Title | Description" per line). Empty lines
// and rows with no title are dropped. Date and description are optional.
// Layout is plain semantic markup; visual styling is applied via the
// `ease-timeline__*` class hooks so the published output stays independent
// of the React-only inline-style preview used inside the editor canvas.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { text } from "../escape.js";

interface TimelineProps {
  events?: string;
}

interface TimelineEvent {
  date: string;
  title: string;
  description: string;
}

function parseEvents(value: unknown): TimelineEvent[] {
  if (typeof value !== "string") return [];
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const [datePart, titlePart, descPart] = line.split("|");
      return {
        date: (datePart ?? "").trim(),
        title: (titlePart ?? "").trim(),
        description: (descPart ?? "").trim(),
      };
    })
    .filter((event) => event.title.length > 0);
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as TimelineProps;

  const events = parseEvents(p.events);
  if (events.length === 0) {
    return "<!-- timeline missing events -->";
  }

  const items = events
    .map((event) => {
      const dateHtml = event.date
        ? `\n        <p class="ease-timeline__date">${text(event.date)}</p>`
        : "";
      const descHtml = event.description
        ? `\n        <p class="ease-timeline__description">${text(event.description)}</p>`
        : "";
      return `      <li class="ease-timeline__item">${dateHtml}
        <p class="ease-timeline__title">${text(event.title)}</p>${descHtml}
      </li>`;
    })
    .join("\n");

  return `<section class="ease-section ease-timeline">
    <ol class="ease-timeline__list" role="list">
${items}
    </ol>
</section>`;
}

registerRenderer("timeline", { render });
